import "./AccountModal.scss";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../../store/authStore";
import { fetcher } from "../../../../utils/fetcher";
import Notification from "../../../Notification/Notification";
import { useTranslation } from "react-i18next";

// Display only - the subscription value itself stays as stored
const SUBSCRIPTION_LABEL_KEYS = {
  free: "billing.plan.free",
  trial: "billing.plan.trial",
  paid: "billing.plan.paid",
  active: "billing.plan.paid", // Legacy mapping if "active" is still in DB
  past_due: "billing.plan.pastDue",
  cancelled: "billing.plan.cancelled",
  canceled: "billing.plan.cancelled",
};

function AccountModal({ onClose }) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const getSubscriptionLabel = (subscription) =>
    t(SUBSCRIPTION_LABEL_KEYS[subscription] || "billing.plan.free");

  const handleCloseAccount = async () => {
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }

    setIsDeleting(true);
    try {
      await fetcher.post("/api/auth/close-account");

      // Log out and redirect to login
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Close account error:", error);
      setNotification({
        message: t("account.closeFailed", {
          error: error.message || t("common.tryAgain"),
        }),
        type: "error",
      });
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("account.notAvailable");

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (error) {
      return t("account.notAvailable");
    }
  };

  return (
    <div className="account-modal-content">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="title">
        <span>{t("userMenu.account")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="account-info-section">
          <div className="info-group">
            <label>{t("common.email")}</label>
            <div className="info-value">{user?.email}</div>
          </div>

          <div className="info-group">
            <label>{t("account.subscriptionStatus")}</label>
            <div className="info-value subscription-status">
              <span className="status-badge">
                {getSubscriptionLabel(user?.subscription)}
              </span>
            </div>
          </div>

          {user?.trialEndsAt && (
            <div className="info-group">
              <label>{t("account.trialEnds")}</label>
              <div className="info-value">{formatDate(user?.trialEndsAt)}</div>
            </div>
          )}
        </div>

        <div className="danger-zone">
          <h3>{t("account.beAware")}</h3>
          <p>{t("account.closeWarning")}</p>
          {showConfirm && (
            <p className="confirm-warning">{t("account.confirmWarning")}</p>
          )}
          <button
            className="close-account-button"
            onClick={handleCloseAccount}
            disabled={isDeleting}
          >
            {isDeleting
              ? t("account.deleting")
              : showConfirm
                ? t("account.clickAgain")
                : t("account.closeAccount")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountModal;
